import React, { useState, useEffect } from 'react';
import { X, Share, Plus, MoreVertical, Download, Smartphone, ArrowDown, ExternalLink } from 'lucide-react';

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isAndroid = () => /Android/.test(navigator.userAgent);
const isSafari = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
const isChrome = () => /Chrome/.test(navigator.userAgent) && !/Edge|Edg/.test(navigator.userAgent);
const isStandalone = () => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
const isMobile = () => /Android|iPhone|iPad|iPod|Opera Mini|IEMobile/i.test(navigator.userAgent);

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showFullGuide, setShowFullGuide] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (isStandalone()) { setInstalled(true); return; }

    // Check if dismissed less than 3 days ago
    const dismissed = localStorage.getItem('pwa_install_dismissed');
    if (dismissed) {
      const daysSince = (Date.now() - parseInt(dismissed)) / (1000 * 60 * 60 * 24);
      if (daysSince < 3) return;
    }

    // Listen for Chrome's beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // On mobile, show banner after 2s even without beforeinstallprompt (for iOS/Firefox)
    let timer;
    if (isMobile()) {
      timer = setTimeout(() => setShowBanner(true), 2000);
    }

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setInstalled(true);
      setShowBanner(false);
      setShowFullGuide(false);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        setInstalled(true);
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    } else {
      setShowFullGuide(true);
    }
  };

  const dismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa_install_dismissed', Date.now().toString());
  };

  if (installed || !showBanner) return null;

  return (
    <>
      {/* ===== Smart Banner (style RBC / Desjardins) ===== */}
      <div className="fixed top-0 left-0 right-0 z-[100] safe-area-top" data-testid="install-banner">
        <div className="bg-white border-b border-gray-200 shadow-lg">
          <div className="flex items-center gap-3 px-4 py-3 max-w-lg mx-auto">
            {/* App Icon */}
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-md">
              <img src="/logo192.png" alt="FV" className="w-12 h-12 rounded-xl"
                onError={(e) => { e.target.style.display = 'none'; }} />
            </div>

            {/* App Info */}
            <div className="flex-1 min-w-0">
              <p className="text-gray-900 font-bold text-base leading-tight">Fleur de Vie</p>
              <p className="text-gray-500 text-xs mt-0.5">fleurde-vie.com</p>
              <div className="flex items-center gap-1 mt-0.5">
                {[1,2,3,4,5].map(s => (
                  <svg key={s} className="w-3 h-3 text-yellow-400 fill-current" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
                <span className="text-gray-400 text-[10px] ml-1">GRATUIT</span>
              </div>
            </div>

            {/* Install Button */}
            <button onClick={handleInstall}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-bold rounded-xl flex-shrink-0 transition-all shadow-sm"
              data-testid="install-btn">
              INSTALLER
            </button>

            {/* Close */}
            <button onClick={dismiss} className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0" data-testid="install-dismiss">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Spacer to push content down */}
      <div className="h-[76px]" />

      {/* ===== Full Installation Guide (Bottom Sheet) ===== */}
      {showFullGuide && (
        <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm flex items-end justify-center" onClick={() => setShowFullGuide(false)}>
          <div className="w-full max-w-md bg-white rounded-t-3xl shadow-2xl animate-in slide-in-from-bottom duration-300" onClick={(e) => e.stopPropagation()}>
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>

            <div className="px-6 pb-8">
              {/* Header */}
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center shadow-lg">
                  <img src="/logo192.png" alt="FV" className="w-14 h-14 rounded-xl" onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
                <div>
                  <h3 className="text-gray-900 font-bold text-xl">Installer Fleur de Vie</h3>
                  <p className="text-gray-500 text-sm">Ajoutez l'app sur votre ecran d'accueil</p>
                </div>
              </div>

              {/* Steps */}
              {isIOS() ? (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">1</div>
                    <div>
                      <p className="text-gray-900 font-semibold text-sm">Appuyez sur le bouton Partager</p>
                      <p className="text-gray-500 text-xs mt-1">L'icone <Share className="w-4 h-4 inline text-blue-500" /> en bas de Safari</p>
                    </div>
                  </div>
                  <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">2</div>
                    <div>
                      <p className="text-gray-900 font-semibold text-sm">Faites defiler et appuyez sur</p>
                      <p className="text-gray-800 text-sm mt-1 flex items-center gap-1"><Plus className="w-4 h-4 text-blue-500" /> <strong>Sur l'ecran d'accueil</strong></p>
                    </div>
                  </div>
                  <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">3</div>
                    <div>
                      <p className="text-gray-900 font-semibold text-sm">Appuyez sur "Ajouter"</p>
                      <p className="text-gray-500 text-xs mt-1">L'application apparaitra sur votre ecran d'accueil</p>
                    </div>
                  </div>

                  {/* Safari indicator arrow */}
                  <div className="text-center pt-2">
                    <ArrowDown className="w-6 h-6 text-blue-500 mx-auto animate-bounce" />
                    <p className="text-blue-600 text-xs font-medium mt-1">Le bouton Partager est en bas</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-100 rounded-2xl p-4 flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">1</div>
                    <div>
                      <p className="text-gray-900 font-semibold text-sm">Menu de Chrome</p>
                      <p className="text-gray-500 text-xs mt-1">Appuyez sur <MoreVertical className="w-4 h-4 inline text-gray-700" /> les 3 points en haut a droite</p>
                    </div>
                  </div>
                  <div className="bg-green-50 border border-green-100 rounded-2xl p-4 flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">2</div>
                    <div>
                      <p className="text-gray-900 font-semibold text-sm">Installer l'application</p>
                      <p className="text-gray-500 text-xs mt-1">Appuyez sur <strong>"Installer l'application"</strong> ou <strong>"Ajouter a l'ecran d'accueil"</strong></p>
                    </div>
                  </div>
                  <div className="bg-green-50 border border-green-100 rounded-2xl p-4 flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">3</div>
                    <div>
                      <p className="text-gray-900 font-semibold text-sm">Confirmez</p>
                      <p className="text-gray-500 text-xs mt-1">Appuyez sur <strong>"Installer"</strong> dans la fenetre</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Alternative: Google Play */}
              <div className="mt-6 pt-4 border-t border-gray-100">
                <p className="text-gray-400 text-xs text-center mb-3">Ou telechargez depuis</p>
                <a href="https://play.google.com/store/apps/details?id=co.median.android.bnawelk" target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center">
                  <img src="https://play.google.com/intl/en_us/badges/static/images/badges/fr_badge_web_generic.png" alt="Disponible sur Google Play" className="h-14" />
                </a>
              </div>

              <button onClick={() => setShowFullGuide(false)}
                className="w-full mt-4 py-3 text-gray-500 text-sm font-medium">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPrompt;
